// eslint-disable-next-line no-restricted-imports
import axios from 'axios';
import { api, accessDBApi } from './api';
import type { ApiResponse, FileSummary, StorageInfo, SyncStatusInfo } from '../types';
import { getOperationErrorMessage } from '../config/errorMessages';

export interface FilesListResponse {
  files: FileSummary[];
  storage: StorageInfo;
  sync_status: SyncStatusInfo;
}

export interface PresignedUrlRequest {
  filename: string;
  content_type: string;
  file_size: number;
}

export interface PresignedUrlResponse {
  file_id: string;
  upload_url: string;
  s3_key: string;
  expires_at: string;
}

export const fileService = {
  // ファイル一覧取得
  getFiles: async (domainId: string): Promise<ApiResponse<FilesListResponse>> => {
    const response = await api.get(`/domains/${domainId}/files`);
    return response.data;
  },

  // プリサインドURL取得
  getPresignedUrl: async (
    domainId: string,
    data: PresignedUrlRequest
  ): Promise<ApiResponse<PresignedUrlResponse>> => {
    const response = await api.post(`/domains/${domainId}/files/upload-url`, data);
    return response.data;
  },

  // S3へファイルアップロード
  uploadToS3: async (uploadUrl: string, file: File, contentType: string): Promise<void> => {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': contentType,
      },
    });
  },

  // アップロード完了確認
  confirmUpload: async (
    domainId: string,
    data: { file_id: string; s3_key: string; filename: string; content_type: string }
  ): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post(`/domains/${domainId}/files/confirm`, data);
    return response.data;
  },

  // ファイル削除
  deleteFile: async (
    domainId: string,
    fileId: string
  ): Promise<ApiResponse<{ message: string; sync_status: string }>> => {
    const response = await api.delete(`/domains/${domainId}/files/${fileId}`);
    return response.data;
  },

  // URLクロール取り込み
  startCrawl: async (
    domainId: string,
    url: string,
    renderMode: 'auto' | 'spa' = 'auto',
  ): Promise<ApiResponse<FileSummary>> => {
    const response = await accessDBApi.post(`/v1/domains/${domainId}/crawl/start`, { url, render_mode: renderMode });
    return response.data;
  },

  // ファイルアップロード（一括処理）
  uploadFile: async (
    domainId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    // 1. プリサインドURL取得
    const presignedResponse = await fileService.getPresignedUrl(domainId, {
      filename: file.name,
      content_type: file.type,
      file_size: file.size,
    });

    if (!presignedResponse.success || !presignedResponse.data) {
      throw new Error(presignedResponse.error?.message || getOperationErrorMessage('upload'));
    }

    const { file_id, upload_url, s3_key } = presignedResponse.data;

    // 2. S3へアップロード
    await axios.put(upload_url, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onProgress(progress);
        }
      },
    });

    // 3. アップロード完了確認
    const confirmResponse = await fileService.confirmUpload(domainId, {
      file_id,
      s3_key,
      filename: file.name,
      content_type: file.type,
    });

    if (!confirmResponse.success) {
      throw new Error(confirmResponse.error?.message || getOperationErrorMessage('upload'));
    }

    return file_id;
  },
};
